import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, BarChart3, RefreshCw, TrendingDown, TrendingUp, Users, UserRound, CalendarRange } from 'lucide-react';
import {
  ComposedChart, BarChart, Bar, Line, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
  ResponsiveContainer, Legend,
} from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, KpiTile, SectionCard, EmptyState, ErrorState, TableSkeleton,
  money, num, plural, fmtDate, fmtDayMonth,
} from '../../components/ui';
import {
  TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TR, TD, TD_NUM, MoneyCell, Notice,
  DateRange, Segmented, CHART, axisMoney, MoneyTooltip, marginLabel, periodLabel,
} from './financeKit';

// LOCAL YYYY-MM-DD — NOT toISOString() (which is UTC and shifts the date back a day for
// AEST users in the morning, silently excluding "today" from the financial window).
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const PRESETS = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'this-quarter', label: 'This quarter' },
  { value: 'this-year', label: 'This year' },
  { value: 'this-fy', label: 'This FY' },
];

function presetRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const today = isoLocal(now);
  if (preset === 'this-month') return { from: isoLocal(new Date(y, m, 1)), to: today };
  if (preset === 'last-month') return { from: isoLocal(new Date(y, m - 1, 1)), to: isoLocal(new Date(y, m, 0)) };
  if (preset === 'this-quarter') { const q = Math.floor(m / 3) * 3; return { from: isoLocal(new Date(y, q, 1)), to: today }; }
  if (preset === 'this-year') return { from: isoLocal(new Date(y, 0, 1)), to: today };
  if (preset === 'this-fy') { const fyY = m >= 6 ? y : y - 1; return { from: isoLocal(new Date(fyY, 6, 1)), to: today }; }
  return null;
}

const SOURCE_LABEL = { shift_rates: 'shift rates', invoice_lines: 'invoice lines', invoice_headers: 'paid invoices', timesheets: 'timesheets' };

// Everything that used to be a 14-link card, as one quiet strip. The sidebar has the same links;
// this keeps them next to the numbers they explain.
const REPORT_LINKS = [
  { label: 'Profit and loss', path: '/financials/reports/profit' },
  { label: 'Staff pay', path: '/financials/reports/staff-pay' },
  { label: 'Contractor pay', path: '/financials/reports/contractors' },
  { label: 'Client funding', path: '/financials/reports/client-funding' },
  { label: 'KM and travel', path: '/financials/reports/km' },
  { label: 'Expenses', path: '/financials/reports/expenses' },
  { label: 'Rates audit', path: '/financials/reports/audit' },
  { label: 'Stakeholders', path: '/financials/reports/stakeholders' },
];
const SETUP_LINKS = [
  { label: 'Client rates', path: '/financials/client-rates' },
  { label: 'Staff rates', path: '/financials/staff-rates' },
  { label: 'Pay contractors', path: '/financials/pay-contractors' },
  { label: 'Travel and transport', path: '/financials/travel-transport' },
  { label: 'Log expenses', path: '/financials/expenses' },
  { label: 'Configuration', path: '/financials/config' },
];

function LinkStrip({ label, links }) {
  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-1 text-[13px]">
      <span className="mr-1 font-medium text-slate-500">{label}</span>
      {links.map((l, i) => (
        <React.Fragment key={l.path}>
          {i > 0 && <span className="text-slate-300" aria-hidden="true">·</span>}
          <Link to={l.path} className="rounded px-1 py-0.5 font-medium text-primary-700 hover:bg-primary-50 hover:text-primary-800">{l.label}</Link>
        </React.Fragment>
      ))}
    </div>
  );
}

const n = (v) => parseFloat(v || 0) || 0;

function ProfitDot({ cx, cy, payload }) {
  if (cx == null || cy == null) return null;
  const loss = n(payload?.net_profit) < 0;
  return <circle cx={cx} cy={cy} r={3.5} fill={loss ? CHART.loss : CHART.profit} stroke="#fff" strokeWidth={1.5} />;
}

export default function FinancialsDashboard() {
  const confirm = useConfirm();
  const [summary, setSummary]   = useState(null);
  const [profit, setProfit]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [recalcing, setRecalcing]     = useState(false);
  const [autoSetting, setAutoSetting] = useState(false);
  const [autoLog, setAutoLog]   = useState(null);

  const [from, setFrom] = useState(() => presetRange('this-month').from);
  const [to, setTo] = useState(() => presetRange('this-month').to);

  // The preset whose range matches the dates on screen, so the active one is visibly on.
  const activePreset = PRESETS.find((p) => {
    const r = presetRange(p.value);
    return r && r.from === from && r.to === to;
  })?.value || null;

  const applyPreset = (preset) => {
    const r = presetRange(preset);
    if (r) { setFrom(r.from); setTo(r.to); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, p] = await Promise.all([
        api.get(`/financials/reports/summary?from=${from}&to=${to}`),
        api.get(`/financials/reports/profit?from=${from}&to=${to}&group=week`),
      ]);
      setSummary(s.data);
      const profitData = p.data;
      setProfit(Array.isArray(profitData) ? profitData : (profitData?.timeline || []));
    } catch (e) {
      setError(e.response?.data?.message || "We couldn't load the financial figures. Check your connection, then try again.");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // Re-pricing changes revenue, cost and profit for every shift in range, so it says how many and
  // what it leaves alone before it runs.
  async function recalculate() {
    let preview = null;
    try {
      const r = await api.get(`/financials/recalculate/preview?from=${from}&to=${to}`);
      preview = r.data || null;
    } catch { /* the confirm below still names the range */ }
    const range = `${fmtDate(from)} – ${fmtDate(to)}`;
    if (preview && preview.total === 0) {
      toast(`There are no shifts dated ${range} to re-price.`);
      return;
    }
    const lines = ['Revenue, staff cost and profit for these shifts are worked out again from the rates set up today.'];
    if (preview?.locked > 0) {
      lines.push(`${plural(preview.locked, 'shift')} ${preview.locked === 1 ? 'is' : 'are'} in the closed period (through ${fmtDate(preview.lockedThrough)}) and stay exactly as ${preview.locked === 1 ? 'it is' : 'they are'}.`);
    }
    if (preview && preview.total > preview.cap) {
      lines.push(`One run re-prices ${num(preview.cap)} shifts, oldest first. Run it again for the rest.`);
    }
    const ok = await confirm(lines.join('\n\n'), {
      title: preview ? `Re-price ${plural(preview.total, 'shift')} dated ${range}?` : `Re-price every shift dated ${range}?`,
      confirmLabel: preview ? `Re-price ${plural(preview.total, 'shift')}` : 'Re-price shifts',
    });
    if (!ok) return;
    setRecalcing(true);
    try {
      const r = await api.post('/financials/recalculate', { from, to });
      const d = r.data || {};
      if (d.capped) {
        toast(`Re-priced ${num(d.recalculated)} of ${num(d.total)} shifts. Run Recalculate again for the remaining ${num(d.remaining)}.`,
          { icon: <AlertTriangle size={16} className="text-amber-500" />, duration: 8000 });
      } else {
        toast.success(`Re-price finished: ${plural(d.recalculated || 0, 'shift')} processed`);
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Recalculation failed');
    } finally {
      setRecalcing(false);
    }
  }

  async function autoSetup() {
    const ok = await confirm(
      "This creates or updates client charge rates from each client's price book and staff cost rates from each person's pay group, replacing any rate already set for the same client or staff member and shift type.\n\nIt then re-prices shifts, up to 2,000 in one run, oldest first. Shifts in a closed period are left as they are.",
      { title: 'Set up rates automatically?', confirmLabel: 'Set up rates', danger: true });
    if (!ok) return;
    setAutoSetting(true);
    setAutoLog(null);
    try {
      const r = await api.post('/financials/auto-setup', {});
      setAutoLog(r.data.log);
      if (r.data.capped) {
        toast(`Rates set up. ${num(r.data.shifts_recalculated)} of ${num(r.data.shifts_total)} shifts re-priced; run it again for the remaining ${num(r.data.shifts_remaining)}.`,
          { icon: <AlertTriangle size={16} className="text-amber-500" />, duration: 8000 });
      } else {
        toast.success(`Rates set up: ${plural(r.data.shifts_recalculated || 0, 'shift')} re-priced`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Auto-setup failed');
    } finally {
      setAutoSetting(false);
    }
  }

  const effectiveRevenue = n(summary?.effective_revenue || summary?.total_revenue);
  const staffPay   = n(summary?.total_staff_pay);
  const superAmt   = n(summary?.total_super);
  const fuel       = n(summary?.total_fuel);
  const expenses   = n(summary?.total_expenses);
  const totalCost  = staffPay + superAmt + fuel + expenses;
  const netProfit  = n(summary?.net_profit);
  const missingRates = (summary?.missing_client_rates || 0) + (summary?.missing_staff_rates || 0);
  const revenueConflict = summary && !summary.rates_configured;
  const openInvoices = (summary?.invoice_count_sent || 0) + (summary?.invoice_count_overdue || 0);
  const topClients = summary?.top_clients || [];
  const topStaff = summary?.top_staff || [];
  const trend = summary?.trend || [];

  return (
    <div className={PAGE_WIDE}>
      {/* Controls — the top bar already names the page. */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <Segmented ariaLabel="Date range presets" options={PRESETS} value={activePreset} onChange={applyPreset} />
          <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} idPrefix="fin" />
        </div>
        <button type="button" onClick={recalculate} disabled={recalcing || loading} className="btn-secondary self-start lg:self-auto">
          <RefreshCw size={14} className={recalcing ? 'animate-spin' : ''} />
          {recalcing ? 'Re-pricing…' : 'Recalculate'}
        </button>
      </div>

      {error ? (
        <div className="card"><ErrorState message={error} onRetry={load} /></div>
      ) : (
        <>
          {/* Source conflict: say where the numbers come from and offer the fix. */}
          {!loading && revenueConflict && (
            <Notice tone="amber"
              title="Shift rates aren't set up, so these figures come from invoices and timesheets"
              actions={(
                <button type="button" onClick={autoSetup} disabled={autoSetting} className="btn-secondary btn-sm">
                  <RefreshCw size={13} className={autoSetting ? 'animate-spin' : ''} />
                  {autoSetting ? 'Setting up…' : 'Set up rates automatically'}
                </button>
              )}>
              Revenue from {SOURCE_LABEL[summary.revenue_source] || summary.revenue_source}, staff cost from {SOURCE_LABEL[summary.staff_cost_source] || summary.staff_cost_source}.
              Setting up rates syncs them from your price books and pay groups, then re-prices shifts.
            </Notice>
          )}
          {autoLog && autoLog.length > 0 && (
            <Notice tone="slate" title="Rate set-up finished" onDismiss={() => setAutoLog(null)}>
              <ul className="list-disc pl-4 space-y-0.5">{autoLog.map((l, i) => <li key={i}>{l}</li>)}</ul>
            </Notice>
          )}

          {!loading && missingRates > 0 && (
            <Notice tone="amber" title={`${plural(missingRates, 'shift')} ${missingRates === 1 ? 'has' : 'have'} a missing rate`}
              actions={<Link to="/financials/reports/audit" className="btn-secondary btn-sm">Open the rates audit</Link>}>
              Profit figures may be understated until every shift has a client charge rate and a staff cost rate.
            </Notice>
          )}

          {/* One quiet row: revenue − total cost = net profit. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile label="Revenue" accent="slate" value={loading ? '…' : money(effectiveRevenue)}
              sub={loading ? null : `From ${SOURCE_LABEL[summary?.revenue_source] || 'invoices'}`} />
            <KpiTile label="Total cost" accent="slate" value={loading ? '…' : money(totalCost)}
              sub={loading ? null : `Staff ${money(staffPay)} · super ${money(superAmt)} · fuel ${money(fuel)} · expenses ${money(expenses)}`} />
            <KpiTile label="Net profit" accent="primary" alert={!loading && netProfit < 0}
              icon={netProfit < 0 ? TrendingDown : TrendingUp}
              value={loading ? '…' : money(netProfit)}
              sub={loading ? null : marginLabel(netProfit, effectiveRevenue)} />
            <KpiTile label="Outstanding" accent="slate" value={loading ? '…' : money(summary?.invoice_outstanding)}
              sub={loading ? null : `${plural(openInvoices, 'invoice')} awaiting payment`} />
          </div>
          {!loading && summary && (
            <p className="text-xs text-slate-500 -mt-3">
              {plural(summary.total_shifts || 0, 'shift')} · {num(summary.total_hours, 1)} h · {num(summary.total_km, 0)} km
              {!revenueConflict && <> · Revenue from {SOURCE_LABEL[summary.revenue_source] || summary.revenue_source} · Staff cost from {SOURCE_LABEL[summary.staff_cost_source] || summary.staff_cost_source}</>}
              {' '}· Counts shifts with an approved timesheet only.
            </p>
          )}

          <nav aria-label="Financial reports and set-up" className="card px-4 py-3 space-y-1.5">
            <LinkStrip label="Reports" links={REPORT_LINKS} />
            <LinkStrip label="Set-up" links={SETUP_LINKS} />
          </nav>

          {/* Weekly revenue against every cost, with net profit on top. */}
          <SectionCard title="Revenue, cost and profit by week" icon={BarChart3} bodyClass="p-4">
            {loading ? (
              <TableSkeleton rows={5} cols={4} />
            ) : profit.length === 0 ? (
              <EmptyState icon={BarChart3} title="No approved shifts in this range"
                hint="Figures appear once shifts in the range have approved timesheets. If shifts are approved but missing, run Recalculate." />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={profit} margin={{ top: 8, right: 8, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                    <XAxis dataKey="period" tick={{ fontSize: 11, fill: CHART.axis }} tickLine={false} tickFormatter={(p) => fmtDayMonth(p)} />
                    <YAxis tick={{ fontSize: 11, fill: CHART.axis }} tickFormatter={axisMoney} width={56} tickLine={false} axisLine={false} />
                    <Tooltip content={<MoneyTooltip labelFormatter={(p) => periodLabel(p, 'week')} />} cursor={{ fill: 'rgba(148,163,184,.12)' }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <ReferenceLine y={0} stroke="#cbd5e1" />
                    <Bar dataKey="revenue" name="Revenue" fill={CHART.revenue} radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Bar dataKey="staff_cost" name="Staff pay" stackId="cost" fill={CHART.staff} maxBarSize={28} />
                    <Bar dataKey="super_amount" name="Super" stackId="cost" fill={CHART.super} maxBarSize={28} />
                    <Bar dataKey="fuel" name="Fuel" stackId="cost" fill={CHART.fuel} maxBarSize={28} />
                    <Bar dataKey="expenses" name="Expenses" stackId="cost" fill={CHART.expenses} radius={[3, 3, 0, 0]} maxBarSize={28} />
                    <Line type="linear" dataKey="net_profit" name="Net profit" stroke={CHART.profit} strokeWidth={2}
                      dot={<ProfitDot />} activeDot={{ r: 5 }} isAnimationActive={false} />
                  </ComposedChart>
                </ResponsiveContainer>
                <p className="text-xs text-slate-500 mt-2">The cost bar stacks staff pay, super, fuel and expenses. A red dot is a week that made a loss.</p>
              </>
            )}
          </SectionCard>

          {/* Top clients + top staff */}
          {!loading && (topClients.length > 0 || topStaff.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {topClients.length > 0 && (
                <SectionCard title="Top clients by revenue" icon={Users}>
                  <div className={TABLE_WRAP}>
                    <table className={TABLE}>
                      <thead>
                        <tr className={THEAD_ROW}>
                          <th scope="col" className={TH}>Client</th>
                          <th scope="col" className={TH_NUM}>Shifts</th>
                          <th scope="col" className={TH_NUM}>Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topClients.map((c, i) => (
                          <tr key={c.id || i} className={TR}>
                            <td className={`${TD} font-medium text-slate-800`}>{c.name}</td>
                            <td className={TD_NUM}>{num(c.shifts)}</td>
                            <td className={TD_NUM}><MoneyCell value={c.revenue} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}

              {topStaff.length > 0 && (
                <SectionCard title="Top staff by hours" icon={UserRound}>
                  <div className={TABLE_WRAP}>
                    <table className={TABLE}>
                      <thead>
                        <tr className={THEAD_ROW}>
                          <th scope="col" className={TH}>Staff</th>
                          <th scope="col" className={TH_NUM}>Shifts</th>
                          <th scope="col" className={TH_NUM}>Hours</th>
                          <th scope="col" className={TH_NUM}>Pay</th>
                          <th scope="col" className={TH_NUM}>Super</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topStaff.map((s, i) => (
                          <tr key={s.id || i} className={TR}>
                            <td className={TD}>
                              <span className="font-medium text-slate-800">{s.name}</span>
                              {s.is_contractor && <span className="badge-gray !text-[10px] ml-2">Contractor</span>}
                            </td>
                            <td className={TD_NUM}>{num(s.shifts)}</td>
                            <td className={TD_NUM}>{num(s.hours, 1)}</td>
                            <td className={TD_NUM}><MoneyCell value={s.pay} /></td>
                            <td className={TD_NUM}>{s.is_contractor ? <span className="text-slate-500">—</span> : <MoneyCell value={s.super_amount} />}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}
            </div>
          )}

          {/* 12-month trend */}
          {!loading && trend.length > 0 && (
            <SectionCard title="Last 12 months" icon={CalendarRange} bodyClass="p-4">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={trend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: CHART.axis }} tickLine={false} tickFormatter={(m) => periodLabel(m, 'month')} />
                  <YAxis tick={{ fontSize: 11, fill: CHART.axis }} tickFormatter={axisMoney} width={56} tickLine={false} axisLine={false} />
                  <Tooltip content={<MoneyTooltip labelFormatter={(m) => periodLabel(m, 'month')} />} cursor={{ fill: 'rgba(148,163,184,.12)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <ReferenceLine y={0} stroke="#cbd5e1" />
                  <Bar dataKey="revenue" name="Revenue" fill={CHART.revenue} radius={[3, 3, 0, 0]} maxBarSize={24} />
                  <Bar dataKey="staff_cost" name="Staff pay" fill={CHART.staff} radius={[3, 3, 0, 0]} maxBarSize={24} />
                  <Bar dataKey="gross_profit" name="Shift profit" fill={CHART.profit} radius={[3, 3, 0, 0]} maxBarSize={24}>
                    {trend.map((t, i) => <Cell key={t.month || i} fill={n(t.gross_profit) < 0 ? CHART.loss : CHART.profit} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-slate-500 mt-2">Shift profit is revenue less staff pay, super and fuel, before business expenses.</p>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}
